import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const supabaseServiceKey = process.env.REACT_APP_SUPABASE_SERVICE_ROLE_KEY || '';

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Admin client with service role key for privileged operations
export const supabaseAdmin = createClient(supabaseUrl, supabaseServiceKey, {
  auth: {
    autoRefreshToken: false,
    persistSession: false
  }
});

// Auth helpers
export const signUp = async (email, password, metadata = {}) => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: metadata,
      emailRedirectTo: window.location.origin
    }
  });
  return { data, error };
};

export const signIn = async (email, password) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password
  });
  return { data, error };
};

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  return { error };
};

// Password reset - send reset email
export const resetPassword = async (email) => {
  const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`
  });
  return { data, error };
};

// Update password (after clicking reset link)
export const updatePassword = async (newPassword) => {
  const { data, error } = await supabase.auth.updateUser({
    password: newPassword
  });
  return { data, error };
};

export const getCurrentUser = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};

// Delete user account (admin only)
export const deleteUserAccount = async (userId) => {
  try {
    console.log('Starting deletion for user:', userId);
    
    // Delete user's saved events
    const { error: savedEventsError } = await supabaseAdmin
      .from('user_saved_events')
      .delete()
      .eq('user_id', userId);
    
    if (savedEventsError) {
      console.log('No saved events to delete or error:', savedEventsError);
    }
    
    // Delete user's favorite speakers
    const { error: favSpeakersError } = await supabaseAdmin
      .from('user_favorite_speakers')
      .delete()
      .eq('user_id', userId);
    
    if (favSpeakersError) {
      console.log('No favorite speakers to delete or error:', favSpeakersError);
    }
    
    // Delete user's event registrations
    const { error: registrationsError } = await supabaseAdmin
      .from('event_registrations')
      .delete()
      .eq('user_id', userId);
    
    if (registrationsError) {
      console.log('No registrations to delete or error:', registrationsError);
    }
    
    // Delete user's submissions
    const { error: submissionsError } = await supabaseAdmin
      .from('speaker_submissions')
      .delete()
      .eq('speaker_id', userId);
    
    if (submissionsError) {
      console.log('No submissions to delete or error:', submissionsError);
    }
    
    // Delete user's profile
    const { error: profileError } = await supabaseAdmin
      .from('profiles')
      .delete()
      .eq('id', userId);
    
    if (profileError) {
      console.error('Error deleting profile:', profileError);
      return { error: profileError };
    }
    
    // Delete user's auth account
    const { error: authError } = await supabaseAdmin.auth.admin.deleteUser(userId);
    
    if (authError) {
      console.error('Error deleting auth user:', authError);
      return { error: authError };
    }
    
    console.log('User deleted successfully');
    return { data: { success: true }, error: null };
  } catch (error) {
    console.error('Error deleting user account:', error);
    return { error };
  }
};

// Deactivate user account (admin only)
export const deactivateUserAccount = async (userId) => {
  try {
    // Check if columns exist, if not, just return success
    const { data, error } = await supabaseAdmin
      .from('profiles')
      .update({ 
        is_active: false,
        deactivated_at: new Date().toISOString()
      })
      .eq('id', userId);
    
    // If columns don't exist yet, that's okay
    if (error && error.message.includes('column') && error.message.includes('does not exist')) {
      console.log('Deactivation columns not created yet - skipping');
      return { data: { success: true }, error: null };
    }
    
    return { data, error };
  } catch (error) {
    console.error('Error deactivating user:', error);
    return { error };
  }
};

// User requests their own account deletion
export const requestAccountDeletion = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { error: { message: 'No user logged in' } };
    }
    
    // Mark for deletion instead of immediate deletion
    const { data, error } = await supabase
      .from('profiles')
      .update({ 
        deletion_requested: true,
        deletion_requested_at: new Date().toISOString()
      })
      .eq('id', user.id);
    
    // If columns don't exist yet, just sign out
    if (error && error.message.includes('column') && error.message.includes('does not exist')) {
      console.log('Deletion columns not created yet - just signing out');
    }
    
    // Sign out the user
    await supabase.auth.signOut();
    
    return { data: { success: true }, error: null };
  } catch (error) {
    console.error('Error requesting account deletion:', error);
    return { error };
  }
};

// Event helpers
export const getEvents = async (filters = {}) => {
  let query = supabase
    .from('events')
    .select(`
      *,
      organization:organizations(id, name, full_name),
      venue:venues(id, name, address, city, state, country)
    `);
  
  if (filters.status) {
    query = query.eq('status', filters.status);
  }
  if (filters.organization) {
    query = query.eq('organization.name', filters.organization);
  }
  if (filters.featured) {
    query = query.eq('is_featured', true);
  }
  
  const { data, error } = await query.order('start_date', { ascending: true });
  return { data, error };
};

export const getEventById = async (id) => {
  const { data, error } = await supabase
    .from('events')
    .select(`
      *,
      organization:organizations(id, name, full_name),
      venue:venues(*),
      event_speakers(
        session_title,
        session_date,
        session_time,
        topic,
        speaker:speakers(*)
      )
    `)
    .eq('id', id)
    .single();
  return { data, error };
};

export const getUpcomingEvents = async (limit = 10) => {
  try {
    const today = new Date().toISOString().split('T')[0];
    const { data, error } = await supabase
      .from('events')
      .select(`
        *,
        organization:organizations(id, name, full_name),
        venue:venues(id, name, address, city, state, country)
      `)
      .gte('start_date', today)
      .not('status', 'in', '("draft","past")')
      .order('start_date', { ascending: true })
      .limit(limit);
    return { data: data || [], error };
  } catch (e) {
    console.error('Error fetching upcoming events:', e);
    return { data: [], error: e };
  }
};

export const getPastEvents = async (limit = 10) => {
  try {
    const today = new Date().toISOString().split('T')[0];
    const { data, error } = await supabase
      .from('events')
      .select(`
        *,
        organization:organizations(id, name, full_name),
        venue:venues(id, name, address, city, state, country)
      `)
      .lt('start_date', today)
      .order('start_date', { ascending: false })
      .limit(limit);
    return { data: data || [], error };
  } catch (e) {
    console.error('Error fetching past events:', e);
    return { data: [], error: e };
  }
};

export const getAllEvents = async () => {
  try {
    const { data, error } = await supabase
      .from('events')
      .select(`
        *,
        organization:organizations(id, name, full_name),
        venue:venues(id, name, address, city, state, country)
      `)
      .neq('status', 'draft')
      .order('start_date', { ascending: true });
    return { data: data || [], error };
  } catch (e) {
    console.error('Error fetching events:', e);
    return { data: [], error: e };
  }
};

export const updateEventStatus = async (eventId, status) => {
  const { data, error } = await supabase
    .from('events')
    .update({ status, updated_at: new Date().toISOString() })
    .eq('id', eventId);
  return { data, error };
};

export const deleteEvent = async (eventId) => {
  const { data, error } = await supabase
    .from('events')
    .delete()
    .eq('id', eventId);
  return { data, error };
};

// Speaker helpers
export const getSpeakers = async () => {
  const { data, error } = await supabase
    .from('speakers')
    .select('*')
    .order('name', { ascending: true });
  return { data, error };
};

export const updateSpeaker = async (speakerId, updates) => {
  const { data, error } = await supabase
    .from('speakers')
    .update(updates)
    .eq('id', speakerId);
  return { data, error };
};

export const deleteSpeaker = async (speakerId) => {
  const { data, error } = await supabase
    .from('speakers')
    .delete()
    .eq('id', speakerId);
  return { data, error };
};

// Organization helpers
export const getOrganizations = async () => {
  const { data, error } = await supabase
    .from('organizations')
    .select('*')
    .order('name', { ascending: true });
  return { data, error };
};

// Venue helpers
export const getVenues = async () => {
  const { data, error } = await supabase
    .from('venues')
    .select('*')
    .order('name', { ascending: true });
  return { data, error };
};

// Event status helpers
export const updateExpiredEvents = async () => {
  // Call the database function to update expired events
  const { data, error } = await supabase.rpc('daily_event_status_update');
  return { data, error };
};

// Helper to determine event status based on dates (client-side fallback)
export const getEventDisplayStatus = (event) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const startDate = new Date(event.start_date || event.start);
  const endDate = new Date(event.end_date || event.end || event.start_date || event.start);
  
  if (event.status === 'cancelled') return 'cancelled';
  if (endDate < today) return 'past';
  if (startDate <= today && endDate >= today) return 'ongoing';
  return event.status || 'upcoming';
};

// ============================================
// Analytics Functions
// ============================================

// Record an event view
export const recordEventView = async (eventId, sessionId = null) => {
  const { data: { user } } = await supabase.auth.getUser();
  const { data, error } = await supabase.rpc('record_event_view', {
    p_event_id: eventId,
    p_user_id: user?.id || null,
    p_session_id: sessionId
  });
  return { data, error };
};

// Record a speaker view
export const recordSpeakerView = async (speakerId, sessionId = null) => {
  const { data: { user } } = await supabase.auth.getUser();
  const { data, error } = await supabase.rpc('record_speaker_view', {
    p_speaker_id: speakerId,
    p_user_id: user?.id || null,
    p_session_id: sessionId
  });
  return { data, error };
};

// Log user activity
export const logUserActivity = async (actionType, entityType = null, entityId = null, metadata = {}) => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { data: null, error: null }; // Skip if not logged in
  
  const { data, error } = await supabase.rpc('log_user_activity', {
    p_user_id: user.id,
    p_action_type: actionType,
    p_entity_type: entityType,
    p_entity_id: entityId,
    p_metadata: metadata
  });
  return { data, error };
};

// Get dashboard analytics (admin only)
export const getDashboardAnalytics = async (days = 30) => {
  const { data, error } = await supabase.rpc('get_dashboard_analytics', {
    p_days: days
  });
  return { data: data?.[0] || null, error };
};

// Get top viewed events (admin only)
export const getTopEvents = async (days = 30, limit = 10) => {
  const { data, error } = await supabase.rpc('get_top_events', {
    p_days: days,
    p_limit: limit
  });
  return { data, error };
};

// Get user growth data (admin only)
export const getUserGrowth = async (days = 30) => {
  const { data, error } = await supabase.rpc('get_user_growth', {
    p_days: days
  });
  return { data, error };
};

// Get views over time (admin only)
export const getViewsOverTime = async (days = 30) => {
  const { data, error } = await supabase.rpc('get_views_over_time', {
    p_days: days
  });
  return { data, error };
};

// Generate a session ID for anonymous tracking
export const getSessionId = () => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substring(2, 15);
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};
